/**
 * Collection Reviews Widget
 *
 * Fetches all products from a collection, retrieves Klaviyo reviews for them,
 * and updates the server-rendered widget with aggregate rating data.
 */
  class CollectionReviews extends HTMLElement {
      constructor() {
        super();
        this.productsPerPage = 250;
      }

      get companyId() {
        return window?.klaviyoModulesObject?.companyId || '';
      }

      connectedCallback() {
        this.collectionHandle = this.dataset.collectionHandle;
        this.totalProducts = parseInt(this.dataset.totalProducts, 10) || 0;

        if (!this.collectionHandle || this.totalProducts === 0) return;
        if (window.Shopify?.designMode) return;
        this.waitForCompanyId().then(() => this.fetchAndRender());
      }

      /**
       * Wait for klaviyoModulesObject.companyId to become available.
       * Polls every 500ms up to 10 seconds, then gives up.
       */
      waitForCompanyId() {
        return new Promise((resolve) => {
          if (this.companyId) {
            resolve();
            return;
          }

          let attempts = 0;
          const maxAttempts = 20;
          const interval = setInterval(() => {
            attempts++;
            if (this.companyId || attempts >= maxAttempts) {
              clearInterval(interval);
              resolve();
            }
          }, 500);
        });
      }

      /**
       * Main flow: fetch all product IDs once → fetch reviews (with retries) → update widget.
       */
      async fetchAndRender() {
        try {
          const productIds = await this.getAllProductIds();

          if (productIds.length === 0) {
            this.renderEmpty();
            return;
          }

          const reviews = await this.getReviewsWithRetry(productIds);
          const stats = this.computeStats(reviews);
          this.renderWidget(stats);
        } catch {
          this.renderEmpty();
        }
      }

      /**
       * Retry fetching reviews up to maxRetries times with increasing delay.
       */
      async getReviewsWithRetry(productIds, attempt = 1) {
        const maxRetries = 3;
        try {
          const reviews = await this.getReviews(productIds);

          if (reviews.length === 0 && attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            return this.getReviewsWithRetry(productIds, attempt + 1);
          }

          return reviews;
        } catch (error) {
          if (attempt < maxRetries) {
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
            return this.getReviewsWithRetry(productIds, attempt + 1);
          }
          throw error;
        }
      }

      /**
       * Fetch all product IDs from the collection across all pages.
       * Deduplication of variants/same-product is handled later by product.name from Klaviyo.
       */
      async getAllProductIds() {
        const totalPages = Math.ceil(this.totalProducts / this.productsPerPage);
        const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

        const requests = pageNumbers.map((page) =>
          fetch(`/collections/${this.collectionHandle}/products.json?page=${page}&limit=${this.productsPerPage}`)
            .then((res) => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.json();
            })
        );

        const results = await Promise.allSettled(requests);
        const productIds = [];

        results.forEach((result) => {
          if (result.status === 'fulfilled' && result.value.products) {
            result.value.products.forEach((product) => {
              productIds.push(String(product.id));
            });
          }
        });

        return productIds;
      }

      /**
       * Fetch reviews using per-product endpoint first (provides rating_histogram).
       * If that returns nothing (Klaviyo doesn't index these IDs individually),
       * fall back to the bulk endpoint which matches by product group/tags.
       */
      async getReviews(productIds) {
        if (!this.companyId) return [];

        const perProduct = await this.fetchPerProduct(productIds);
        if (perProduct.length > 0) return perProduct;
        return this.fetchBulk(productIds);
      }

      /**
       * Per-product endpoint: returns rating_histogram for exact distribution.
       * Deduplicates by product.name when available.
       */
      async fetchPerProduct(productIds) {
        const results = await Promise.allSettled(
          productIds.map((id) =>
            fetch(
              `https://fast.a.klaviyo.com/reviews/api/client_reviews/${id}/?product_id=${id}&company_id=${this.companyId}&limit=1`
            ).then((res) => {
              if (!res.ok) throw new Error(`Klaviyo HTTP ${res.status}`);
              return res.json();
            })
          )
        );

        const seenNames = new Set();
        return results.reduce((acc, result) => {
          if (result.status !== 'fulfilled') return acc;

          const { product, summary } = result.value;
          const data = parseInt(product?.review_count, 10) > 0 ? product : summary;

          if (!(parseInt(data?.review_count, 10) > 0)) return acc;

          if (data.name) {
            if (seenNames.has(data.name)) return acc;
            seenNames.add(data.name);
          }

          acc.push(data);
          return acc;
        }, []);
      }

      /**
       * Bulk endpoint fallback: matches products by Klaviyo's internal grouping (tags/name).
       * Returns { shopify_id, star_rating, review_count } — no rating_histogram.
       * computeStats will approximate distribution from star_rating.
       */
      async fetchBulk(productIds) {
        const chunkSize = 100;
        const chunks = [];
        for (let i = 0; i < productIds.length; i += chunkSize) {
          chunks.push(productIds.slice(i, i + chunkSize));
        }

        const results = await Promise.allSettled(
          chunks.map(async (chunk) => {
            // Try integer IDs — Klaviyo may reject quoted string IDs
            const idsAsIntegers = chunk.map((id) => parseInt(id, 10));
            const idsParam = encodeURIComponent(JSON.stringify(idsAsIntegers));
            const url = `https://fast.a.klaviyo.com/reviews/api/client_reviews/?company_id=${this.companyId}&products=${idsParam}`;
            const res = await fetch(url);
            if (!res.ok) throw new Error(`Klaviyo bulk HTTP ${res.status}`);
            return res.json();
          })
        );

        const seenIds = new Set();
        return results.reduce((acc, result) => {
          if (result.status !== 'fulfilled') return acc;
          const products = result.value.products || [];
          products.forEach((p) => {
            if (!(parseInt(p.review_count, 10) > 0)) return;
            if (seenIds.has(p.shopify_id)) return;
            seenIds.add(p.shopify_id);
            acc.push(p);
          });
          return acc;
        }, []);
      }

      /**
       * Compute aggregate stats from Klaviyo product objects.
       * Each product has { star_rating, rating_histogram, review_count }.
       * rating_histogram is [1-star count, 2-star count, 3-star count, 4-star count, 5-star count].
       * Weighted average = sum(star_rating * review_count) / sum(review_count).
       */
      computeStats(products) {
        const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
        let weightedSum = 0;
        let totalCount = 0;

        products.forEach((product) => {
          const rating = parseFloat(product.star_rating);
          const count = parseInt(product.review_count, 10);
          const histogram = product.rating_histogram;

          if (!count || count <= 0) return;

          weightedSum += rating * count;
          totalCount += count;

          if (Array.isArray(histogram) && histogram.length === 5) {
            // Exact distribution from per-product endpoint
            histogram.forEach((starCount, index) => {
              const star = index + 1; // index 0 = 1-star, index 4 = 5-star
              distribution[star] += starCount || 0;
            });
          } else if (rating >= 1) {
            // Approximate distribution from star_rating (bulk fallback)
            const floor = Math.max(1, Math.min(5, Math.floor(rating)));
            const ceil = Math.min(5, floor + 1);
            if (floor === ceil) {
              distribution[floor] += count;
            } else {
              const fraction = rating - floor;
              distribution[ceil] += Math.round(count * fraction);
              distribution[floor] += count - Math.round(count * fraction);
            }
          }
        });

        const average = totalCount > 0 ? (weightedSum / totalCount).toFixed(1) : '0.0';

        return { average: parseFloat(average), totalCount, distribution };
      }

      /**
       * Update the server-rendered widget with review data.
       */
      renderWidget({ average, totalCount, distribution }) {
        if (totalCount === 0) {
          this.renderEmpty();
          return;
        }

        const avgEl = this.querySelector('[data-cr-average]');
        if (avgEl) avgEl.textContent = average;

        const floorRating = Math.floor(average);
        const fraction = Math.round((average - floorRating) * 100);

        this.querySelectorAll('[data-cr-star]').forEach((star) => {
          const i = parseInt(star.dataset.crStar, 10);

          if (i <= floorRating) {
            star.classList.add('cr-star--filled');
          } else if (i === floorRating + 1 && fraction > 0) {
            star.classList.add('cr-star--partial');
            const fill = star.querySelector('.cr-star__fill');
            if (fill) fill.style.width = `${fraction}%`;
          } else {
            star.classList.add('cr-star--empty');
          }
        });

        const countEl = this.querySelector('[data-cr-count]');
        if (countEl) countEl.textContent = `${totalCount} rating${totalCount !== 1 ? 's' : ''}`;

        [5, 4, 3, 2, 1].forEach((star) => {
          const count = distribution[star];
          const percentage = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;

          const fill = this.querySelector(`[data-cr-bar-fill="${star}"]`);
          if (fill) {
            fill.style.setProperty('--cr-fill-width', `${percentage}%`);
            if (percentage > 0) fill.classList.add('cr-bar-fill--visible');
          }

          const pct = this.querySelector(`[data-cr-bar-percent="${star}"]`);
          if (pct) pct.textContent = `${percentage}%`;
        });

        const loader = this.querySelector('[data-cr-loader]');
        const widget = this.querySelector('[data-cr-widget]');
        if (loader) loader.classList.add('hidden');
        if (widget) widget.classList.remove('hidden');
      }

      renderEmpty() {
        const loader = this.querySelector('[data-cr-loader]');
        const widget = this.querySelector('[data-cr-widget]');
        if (loader) loader.classList.add('hidden');
        if (widget) widget.classList.add('hidden');
        this.style.minHeight = '0';
      }
    }


customElements.define('collection-reviews', CollectionReviews)
